import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Phone, Euro, FileText, CheckCircle2, ArrowRight, Calculator, Users } from 'lucide-react';
import { Button } from '../components/ui/button';

const pflegegradData = {
  '1': {
    title: 'Pflegegrad 1',
    subtitle: 'Geringe Beeinträchtigung der Selbstständigkeit',
    sachleistung: '0€',
    pflegegeld: '0€',
    entlastung: '125€',
    hours: '0',
    beispiele: [
      'Kann sich noch weitgehend selbst versorgen',
      'Benötigt minimale Unterstützung im Alltag',
      'Keine regelmäßige Pflege nötig',
      'Beratung und Hilfestellung ausreichend'
    ],
    typisch: 'Pflegegrad 1 wird in Itzehoe oft für Senioren vergeben, die präventive Unterstützung brauchen. Kein Pflegegeld, aber 125€ Entlastungsbetrag monatlich - nutzbar für Haushaltshilfe oder Betreuungsangebote.',
    keywords: ['pflegegrad 1 leistungen', 'pflegegrad 1 geld', 'pflegegrad 1 entlastungsbetrag']
  },
  '2': {
    title: 'Pflegegrad 2',
    subtitle: 'Erhebliche Beeinträchtigung der Selbstständigkeit',
    sachleistung: '761€',
    pflegegeld: '332€',
    hours: '17',
    beispiele: [
      'Benötigt Hilfe bei Körperpflege 1-2× täglich',
      'Kann sich noch teilweise selbst versorgen',
      'Braucht Unterstützung bei Mobilität',
      'Medikamente unter Anleitung möglich'
    ],
    typisch: 'Viele Senioren in Itzehoe mit Pflegegrad 2 leben noch recht selbstständig, benötigen aber tägliche Unterstützung bei Körperpflege und Haushalt. Oft reicht die Kassenleistung von 761€ vollständig aus.',
    keywords: ['pflegegrad 2 leistungen', 'pflegegrad 2 geld', 'pflegegrad 2 sachleistung']
  },
  '3': {
    title: 'Pflegegrad 3',
    subtitle: 'Schwere Beeinträchtigung der Selbstständigkeit',
    sachleistung: '1.432€',
    pflegegeld: '573€',
    hours: '32',
    beispiele: [
      'Tägliche Hilfe bei Körperpflege mehrmals nötig',
      'Mobilität stark eingeschränkt',
      'Demenz im mittleren Stadium möglich',
      'Medikamentengabe durch Pflegedienst'
    ],
    typisch: 'Pflegegrad 3 ist im Kreis Steinburg sehr häufig. Die 1.432€ Sachleistung ermöglichen etwa 30 Stunden professionelle Pflege pro Monat - für die meisten unserer Klienten in Itzehoe ausreichend.',
    keywords: ['pflegegrad 3 leistungen', 'pflegegrad 3 geld', 'pflegegrad 3 sachleistung']
  },
  '4': {
    title: 'Pflegegrad 4',
    subtitle: 'Schwerste Beeinträchtigung der Selbstständigkeit',
    sachleistung: '1.778€',
    pflegegeld: '765€',
    hours: '39',
    beispiele: [
      'Vollständige Übernahme der Körperpflege',
      'Meist bettlägerig oder im Rollstuhl',
      'Schwere Demenz',
      'Intensive medizinische Pflege nötig'
    ],
    typisch: 'Bei Pflegegrad 4 betreuen wir in Itzehoe oft Menschen nach Schlaganfall oder mit fortgeschrittener Demenz. Die 1.778€ Sachleistung deckt etwa 35-40 Stunden Pflege ab - meist kombinieren wir verschiedene Leistungen.',
    keywords: ['pflegegrad 4 leistungen', 'pflegegrad 4 geld', 'pflegegrad 4 sachleistung']
  },
  '5': {
    title: 'Pflegegrad 5',
    subtitle: 'Schwerste Beeinträchtigung mit besonderen Anforderungen',
    sachleistung: '2.200€',
    pflegegeld: '947€',
    hours: '49',
    beispiele: [
      'Rund-um-die-Uhr Betreuung erforderlich',
      'Vollständige Pflegeabhängigkeit',
      'Meist Bettlägerigkeit',
      'Künstliche Ernährung oder Beatmung möglich'
    ],
    typisch: 'Pflegegrad 5 ist selten, aber bei uns im Kreis Steinburg betreuen wir mehrere Familien damit. Die 2.200€ Sachleistung ermöglichen etwa 45-50 Stunden intensive Pflege monatlich. Oft kombinieren wir mit 24h-Betreuung durch Angehörige.',
    keywords: ['pflegegrad 5 leistungen', 'pflegegrad 5 geld', 'pflegegrad 5 sachleistung', 'höchster pflegegrad']
  }
};

export default function PflegegradSeite({ grad = '2' }) {
  const data = pflegegradData[grad];
  
  if (!data) return null;

  return (
    <>
      <Helmet>
        <title>{data.title} Leistungen & Geld 2026 | Itzehoe | Phönix Pflegeteam</title>
        <meta name="description" content={`${data.title}: ${data.sachleistung}/Monat Sachleistung oder ${data.pflegegeld} Pflegegeld ✓ Was steht Ihnen zu? ✓ Kostenlose Beratung Itzehoe ✓ Hilfe bei Antrag & Höherstufung`} />
        <meta name="keywords" content={data.keywords.join(', ')} />
        <link rel="canonical" href={`https://pflegeteam-itzehoe.de/pflegegrad-${grad}`} />
      </Helmet>

      <main id="main-content" className="pt-32 pb-20">
        <div className="container-custom max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-[#64748b] mb-8">
            <Link to="/" className="hover:text-[#1A52D8]">Home</Link>
            <span>/</span>
            <Link to="/ratgeber" className="hover:text-[#1A52D8]">Ratgeber</Link>
            <span>/</span>
            <span className="text-[#1e293b]">{data.title}</span>
          </nav>

          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-[#1A52D8] to-[#23D3D3] text-white text-4xl font-bold mb-6 shadow-lg">
              {grad}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#1e293b] mb-4">
              {data.title}
            </h1>
            <p className="text-xl text-[#64748b]">{data.subtitle}</p>
          </div>

          {/* Direct Answer Box */}
          <div className="bg-gradient-to-br from-[#f8fafb] to-white rounded-2xl p-8 border-2 border-[#1A52D8]/20 mb-12 direct-answer">
            <h2 className="text-2xl font-bold text-[#1A52D8] mb-4">Was steht Ihnen zu?</h2>
            {grad === '1' ? (
              <div>
                <div className="text-sm text-[#64748b] mb-1">Monatlicher Entlastungsbetrag:</div>
                <div className="text-4xl font-bold text-[#23D3D3]">{data.entlastung}</div>
                <div className="text-sm text-[#64748b] mt-1">Für Betreuung & Haushaltshilfe</div>
                <p className="text-[#475569] mt-4">
                  <strong>Wichtig:</strong> Pflegegrad 1 gibt kein Pflegegeld, aber der Entlastungsbetrag kann für unsere Hauswirtschafts- und Betreuungsleistungen genutzt werden.
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <div className="text-sm text-[#64748b] mb-1">Monatliche Sachleistung:</div>
                  <div className="text-4xl font-bold text-[#1A52D8]">{data.sachleistung}</div>
                  <div className="text-sm text-[#64748b] mt-1">≈ {data.hours} Stunden Pflege/Monat</div>
                </div>
                <div>
                  <div className="text-sm text-[#64748b] mb-1">Alternativ Pflegegeld:</div>
                  <div className="text-4xl font-bold text-[#23D3D3]">{data.pflegegeld}</div>
                  <div className="text-sm text-[#64748b] mt-1">Für selbst organisierte Pflege</div>
                </div>
              </div>
            )}
            {grad !== '1' && (
              <div className="mt-6 pt-6 border-t border-[#e2e8f0]">
                <p className="text-[#475569]">
                  <strong>Kombinationsleistung möglich:</strong> Sie können Sachleistung und Pflegegeld kombinieren 
                  (z.B. 50% Sachleistung + 50% Pflegegeld).
                </p>
              </div>
            )}
          </div>

          {/* Was bedeutet das konkret */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-[#1e293b] mb-6">
              Was bedeutet {data.title} konkret?
            </h2>
            <div className="bg-white rounded-xl p-6 border border-[#e2e8f0]">
              <ul className="space-y-3">
                {data.beispiele.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#23D3D3] mt-0.5 flex-shrink-0" />
                    <span className="text-[#475569]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Lokaler Bezug */}
          <div className="mb-12 bg-[#E8F8F8] rounded-xl p-6">
            <h3 className="font-bold text-[#1A52D8] mb-3 flex items-center gap-2">
              <Users className="w-5 h-5" />
              {data.title} in Itzehoe und Umgebung
            </h3>
            <p className="text-[#475569] leading-relaxed">
              {data.typisch}
            </p>
          </div>

          {/* Rechenbeispiel */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-[#1e293b] mb-6">
              Rechenbeispiel {data.title}
            </h2>
            <div className="bg-white rounded-xl p-8 border border-[#e2e8f0]">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-[#64748b]">Grundpflege (15 Std/Monat):</span>
                  <span className="font-bold text-[#1e293b]">675€</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#64748b]">Behandlungspflege (5 Std/Monat):</span>
                  <span className="font-bold text-[#1e293b]">275€</span>
                </div>
                <div className="h-px bg-[#e2e8f0]"></div>
                <div className="flex justify-between items-center text-lg">
                  <span className="font-semibold text-[#1e293b]">Gesamtkosten:</span>
                  <span className="font-bold text-[#1e293b]">950€</span>
                </div>
                <div className="flex justify-between items-center text-lg">
                  <span className="font-semibold text-[#23D3D3]">Pflegekasse zahlt:</span>
                  <span className="font-bold text-[#23D3D3]">{data.sachleistung}</span>
                </div>
                <div className="h-px bg-[#e2e8f0]"></div>
                <div className="flex justify-between items-center text-xl">
                  <span className="font-bold text-[#1e293b]">Ihr Eigenanteil:</span>
                  <span className="font-bold text-green-600">
                    {grad === '2' ? '0€' : grad === '3' ? '0€' : '0€'}
                  </span>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-[#e2e8f0] text-center">
                <p className="text-sm text-[#64748b]">
                  Dieses Beispiel zeigt: Die Kassenleistung reicht in vielen Fällen vollständig aus!
                </p>
              </div>
            </div>
          </div>

          {/* So helfen wir Ihnen */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-[#1e293b] mb-6">
              So unterstützen wir Sie mit {data.title} in Itzehoe
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 border border-[#e2e8f0]">
                <FileText className="w-10 h-10 text-[#1A52D8] mb-4" />
                <h3 className="font-bold text-[#1e293b] mb-2">Antrag & Höherstufung</h3>
                <p className="text-sm text-[#64748b]">
                  Wir helfen Ihnen kostenlos bei der Beantragung oder Höherstufung des Pflegegrades.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-[#e2e8f0]">
                <Calculator className="w-10 h-10 text-[#23D3D3] mb-4" />
                <h3 className="font-bold text-[#1e293b] mb-2">Kostenberatung</h3>
                <p className="text-sm text-[#64748b]">
                  Wir rechnen genau durch: Was kostet die Pflege? Was zahlt die Kasse? Was bleibt für Sie?
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-[#e2e8f0]">
                <CheckCircle2 className="w-10 h-10 text-[#1A52D8] mb-4" />
                <h3 className="font-bold text-[#1e293b] mb-2">Kassenabrechnung</h3>
                <p className="text-sm text-[#64748b]">
                  Wir rechnen direkt mit der Kasse ab. Sie müssen nicht in Vorkasse gehen.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-[#1A52D8] to-[#23D3D3] rounded-2xl p-8 md:p-12 text-center text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Haben Sie {data.title} oder wollen ihn beantragen?
            </h3>
            <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
              Wir beraten Sie kostenlos zu Ihren Ansprüchen und unterstützen Sie bei der Antragstellung. 
              In Itzehoe und dem ganzen Kreis Steinburg.
            </p>
            <a href="tel:+4915146173685">
              <Button className="bg-white text-[#1A52D8] hover:bg-white/90 h-14 px-10 rounded-full text-lg font-semibold shadow-xl">
                <Phone className="w-5 h-5" />
                Jetzt beraten lassen: 0151 46173685
              </Button>
            </a>
          </div>

          {/* Weitere Pflegegrade */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-[#1e293b] mb-6 text-center">
              Weitere Pflegegrade
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {['1', '2', '3', '4', '5'].map((g) => (
                <Link 
                  key={g}
                  to={`/pflegegrad-${g}`}
                  className={`text-center py-6 rounded-xl border-2 transition-all ${
                    g === grad 
                      ? 'border-[#1A52D8] bg-[#1A52D8]/5' 
                      : 'border-[#e2e8f0] hover:border-[#1A52D8] hover:bg-[#f8fafb]'
                  }`}
                >
                  <div className="text-3xl font-bold text-[#1A52D8] mb-2">PG {g}</div>
                  <div className="text-sm text-[#64748b]">
                    {g === '1' && '125€'}
                    {g === '2' && '761€'}
                    {g === '3' && '1.432€'}
                    {g === '4' && '1.778€'}
                    {g === '5' && '2.200€'}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

// Wrapper-Komponenten für Routes
export function Pflegegrad1() { return <PflegegradSeite grad="1" />; }
export function Pflegegrad2() { return <PflegegradSeite grad="2" />; }
export function Pflegegrad3() { return <PflegegradSeite grad="3" />; }
export function Pflegegrad4() { return <PflegegradSeite grad="4" />; }
export function Pflegegrad5() { return <PflegegradSeite grad="5" />; }
